import React, { useState } from 'react';
import { Instagram, Facebook, X, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useContactInfo } from '../services/remoteConfig';
import waIcon from '../assets/social/whatsapp.png';
import igIcon from '../assets/social/instagram.png';
import fbIcon from '../assets/social/facebook.png';
import ttIcon from '../assets/social/tiktok.png';

// Helper — Remote Config value is either a vanity slug ("emercadohub") or
// a path segment ("profile.php?id=N") for FB pages without a custom URL.
// Pre-prefixed full URLs pass through unchanged.
const socialUrl = (base: string, val: string) =>
  val.startsWith('http') ? val : `${base}/${val}`;

// TikTok handle URL composer — strip leading @ if present, prefix @
// when building the URL path (TikTok routes are /@handle).
const tiktokUrl = (val: string) =>
  val.startsWith('http') ? val : `https://tiktok.com/@${val.replace(/^@/, '')}`;

// WebView detection: customer mobile WebView seeds utm_source on
// entry; sessionStorage carries it across navigations. WebViews
// typically lack a mailto handler so we fall back to clipboard.
function isInsideWebView(): boolean {
  if (typeof window === 'undefined') return false;
  const src = sessionStorage.getItem('utm_source');
  return !!src && src !== 'web' && src !== 'direct';
}

function ContactPopup({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('footer');
  const c = useContactInfo();
  const waDigits = (c.whatsapp || '').replace(/\D/g, '');
  const inWebView = isInsideWebView();

  const handleEmailClick = async (e: React.MouseEvent) => {
    if (!inWebView) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(c.email);
      alert(`Email copied: ${c.email}`);
    } catch {
      alert(c.email);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{t('contact')}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-3">
          {c.whatsapp && (
            <a
              href={`https://wa.me/${waDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <img src={waIcon} alt="WhatsApp" className="h-10 w-10 rounded-full" />
              <div>
                <p className="text-sm font-medium text-gray-900">WhatsApp</p>
                <p className="text-xs text-gray-500">{c.whatsapp}</p>
              </div>
            </a>
          )}

          {c.email && (
            <a
              href={`mailto:${c.email}`}
              onClick={handleEmailClick}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Email</p>
                <p className="text-xs text-gray-500">{c.email}</p>
              </div>
            </a>
          )}

          {c.instagram && (
            <a
              href={socialUrl('https://instagram.com', c.instagram)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <img src={igIcon} alt="Instagram" className="h-10 w-10 rounded-full" />
              <div>
                <p className="text-sm font-medium text-gray-900">Instagram</p>
                <p className="text-xs text-gray-500">@{c.instagram}</p>
              </div>
            </a>
          )}

          {c.facebook && (
            <a
              href={socialUrl('https://facebook.com', c.facebook)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <img src={fbIcon} alt="Facebook" className="h-10 w-10 rounded-full" />
              <div>
                <p className="text-sm font-medium text-gray-900">Facebook</p>
                <p className="text-xs text-gray-500">Emercado TL</p>
              </div>
            </a>
          )}

          {c.tiktok && (() => {
            const raw = c.tiktok;
            const isUrl = raw.startsWith('http');
            const handle = isUrl
              ? (raw.match(/@([^/?#]+)/)?.[1] || raw.replace(/^https?:\/\//, ''))
              : raw.replace(/^@/, '');
            return (
              <a
                href={tiktokUrl(c.tiktok)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <img src={ttIcon} alt="TikTok" className="h-10 w-10 rounded-full" />
                <div>
                  <p className="text-sm font-medium text-gray-900">TikTok</p>
                  <p className="text-xs text-gray-500">@{handle}</p>
                </div>
              </a>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const Footer: React.FC = () => {
  const [showContact, setShowContact] = useState(false);
  const { t } = useTranslation('footer');
  const c = useContactInfo();

  const ecommerceUrl = process.env.REACT_APP_CUSTOMER_URL || 'https://emercadohub.com';

  // Hide footer when embedded (inside a webview app)
  const utmSource = sessionStorage.getItem('utm_source');
  const isEmbedded = !!utmSource && utmSource !== 'web' && utmSource !== 'direct';
  if (isEmbedded) return null;

  return (
    <>
      <footer className="bg-primary text-white py-8 md:py-12">
        <div className="container mx-auto px-4">
          {/* Desktop Layout */}
          <div className="hidden md:block">
            {/* Top Row */}
            <div className="flex items-start justify-between mb-8">
              {/* Left — Wordmark logo carries the brand name as art so we
                  drop the standalone span. logo-text.png is the white
                  outline variant; reads cleanly on the emerald footer
                  background without an extra contrast layer. */}
              <div className="flex items-center space-x-4">
                <img src="/logo-text.png" alt="E-Mercado Hub" className="h-3 w-auto brightness-0 invert" />
                <span className="text-white/80">|</span>
                <span className="text-white/80">{t('tagline')}</span>
              </div>

              {/* Right - Links */}
              <div className="flex items-start gap-12">
                <div className="space-y-2">
                  <p className="font-medium text-sm mb-3">{t('company')}</p>
                  <a href={`${ecommerceUrl}/about`} className="block text-white/80 hover:text-white text-sm transition-colors">
                    {t('about')}
                  </a>
                  <a href={`${ecommerceUrl}/about#privacy-policy`} className="block text-white/80 hover:text-white text-sm transition-colors">
                    {t('privacy_policy')}
                  </a>
                  <a href={`${ecommerceUrl}/about#tnc-voucher-game`} className="block text-white/80 hover:text-white text-sm transition-colors">
                    {t('terms')}
                  </a>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-sm mb-3">{t('support')}</p>
                  <button
                    onClick={() => setShowContact(true)}
                    className="block text-white/80 hover:text-white text-sm transition-colors"
                  >
                    {t('contact')}
                  </button>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/20 mb-6"></div>

            {/* Bottom Row */}
            <div className="flex items-center justify-between">
              <p className="text-white/80 text-sm">
                © {new Date().getFullYear()} E-Mercado Hub. {t('rights')}
              </p>

              <div className="flex items-center space-x-4">
                <a
                  href={socialUrl('https://instagram.com', c.instagram)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href={socialUrl('https://facebook.com', c.facebook)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <Facebook size={20} />
                </a>
                {c.tiktok && (
                  <a
                    href={tiktokUrl(c.tiktok)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors"
                    aria-label="TikTok"
                  >
                    <img src={ttIcon} alt="TikTok" className="h-5 w-5 brightness-0 invert" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden text-center space-y-6">
            <div className="flex flex-col items-center space-y-2">
              <img src="/logo-text.png" alt="E-Mercado Hub" className="h-3 w-auto brightness-0 invert" />
              <span className="text-white/80 text-sm">{t('tagline')}</span>
            </div>

            <div className="flex flex-col space-y-3">
              <a href={`${ecommerceUrl}/about`} className="text-white hover:text-white/80 transition-colors">
                {t('about')}
              </a>
              <a href={`${ecommerceUrl}/about#privacy-policy`} className="text-white hover:text-white/80 transition-colors">
                {t('privacy_policy')}
              </a>
              <a href={`${ecommerceUrl}/about#tnc-voucher-game`} className="text-white hover:text-white/80 transition-colors">
                {t('terms')}
              </a>
              <button
                onClick={() => setShowContact(true)}
                className="text-white hover:text-white/80 transition-colors"
              >
                {t('contact')}
              </button>
            </div>

            <div className="border-t border-white/20"></div>

            <div className="flex items-center justify-center space-x-6">
              <a
                href={socialUrl('https://instagram.com', c.instagram)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors"
              >
                <Instagram size={24} />
              </a>
              <a
                href={socialUrl('https://facebook.com', c.facebook)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors"
              >
                <Facebook size={24} />
              </a>
              {c.tiktok && (
                <a
                  href={tiktokUrl(c.tiktok)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors"
                  aria-label="TikTok"
                >
                  <img src={ttIcon} alt="TikTok" className="h-6 w-6 brightness-0 invert" />
                </a>
              )}
            </div>

            <p className="text-white/80 text-sm">
              © {new Date().getFullYear()} E-Mercado Hub. {t('rights')}
            </p>
          </div>
        </div>
      </footer>

      {showContact && <ContactPopup onClose={() => setShowContact(false)} />}
    </>
  );
};

export default Footer;
